import { useEffect, useState, type FC } from 'react'

import { ExternalLink, Input, LoaderCircle, Popup, Select, Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { COUNTRY_FLAGS, countryOptions, getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import injuryReportsResources from '@/api/injuryReports'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { LocalDateTime } from '@/components/LocalDateTime'
import DataPoint, { DataPoints } from '@/components/primitives/DataPoint'
import { useProfile } from '@/hooks/user'
import { FORM_URL } from '@/util/form'
import { getFormatterMobileNumber } from '@/util/formatters/text'

import { extractCommentFromInjuryReport, getInjuryReportStatusTextId } from '../helpers'

import InjuryReportOptions from './InjuryReportOptions'

const InjuryReport: FC = () => {
  const { whiteLabel } = useAppContext()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const translate = useTranslation()
  const profile = useProfile()
  const aliases = profile?.aliases

  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [smsRecipient, setSmsRecipient] = useState('')
  const [smsCountryId, setSmsCountryId] = useState<number | null>(null)
  const [smsMessage, setSmsMessage] = useState('')
  const [selectedAlias, setSelectedAlias] = useState<number | null>(null)
  const [smsError, setSmsError] = useState<string | null>(null)
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false)

  const { data: injuryReport, isLoading } = useQuery(
    queries.injuryReport(Number(id)),
    () => injuryReportsResources.get(Number(id)),
    {
      enabled: Boolean(id),
    }
  )

  const addCommentMutation = useMutation({
    mutationFn: (comment: string) =>
      injuryReportsResources.addComment({ injuryReportId: Number(id), comment }),
    onSuccess: () => {
      setComment('')
      setError(null)

      // Invalidate and refetch the injury report query
      queryClient.invalidateQueries({
        queryKey: queries.injuryReport(Number(id)),
      })
    },
    onError: (error: any) => {
      // Handle different types of errors
      let errorMessage = translate('Failed to add comment. Please try again.')

      if (error?.response?.status === 404) {
        errorMessage = translate('Injury report not found.')
      } else if (error?.response?.status === 403) {
        errorMessage = translate('You do not have permission to add comments to this report.')
      } else if (error?.response?.status === 400) {
        errorMessage =
          error?.response?.data?.message ||
          translate('Invalid comment data. Please check your input.')
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      setError(errorMessage)
    },
  })

  const sendSmsMutation = useMutation({
    mutationFn: ({
      aliasId,
      recipientCountryId,
      recipientNumber,
      message,
    }: {
      aliasId: number
      recipientCountryId: number
      recipientNumber: string
      message: string
    }) =>
      injuryReportsResources.sendSms({
        injuryReportId: Number(id),
        aliasId,
        recipientCountryId,
        recipientNumber,
        message,
      }),
    onSuccess: () => {
      setSmsMessage('')
      setSmsError(null)
      setIsSmsModalOpen(false)
    },
    onError: () => {
      setSmsError(translate('Could not send sms'))
    },
  })

  const handleAddComment = () => {
    if (!comment.trim()) {
      setError('Please enter a comment before submitting.')
      return
    }

    if (comment.trim().length < 3) {
      setError('Comment must be at least 3 characters long.')
      return
    }

    if (comment.trim().length > 2000) {
      setError('Comment cannot exceed 2000 characters.')
      return
    }

    // Clear any previous errors
    setError(null)

    // Execute the mutation
    addCommentMutation.mutate(comment.trim())
  }

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value)

    // Clear error when user starts typing
    if (error) {
      setError(null)
    }
  }

  useEffect(() => {
    const firstAliasId = aliases?.[0]?.id

    if (!selectedAlias && firstAliasId) {
      setSelectedAlias(firstAliasId)
    }
  }, [aliases, selectedAlias])

  const isSubmitting = addCommentMutation.isLoading

  if (isLoading) return <LoaderCircle whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />
  if (!injuryReport || !id) return null

  const {
    phoneNumber,
    userName,
    assetDescription,
    assetImeiSnr,
    type,
    damageCause,
    damageDescription,
    damageLocation,
    injuryDate,
    created,
    updated,
    policeReportRef,
    insuranceRef,
    editorName,
    creatorName,
    statusId,
    code,
    dealerComment,
    phoneCountryId,
  } = injuryReport

  const handleOpenSmsModal = () => {
    setSmsError(null)
    setSmsRecipient(phoneNumber || '')
    setSmsCountryId(phoneCountryId || null)
    setIsSmsModalOpen(true)
  }

  const handleSendSms = () => {
    const recipientNumber = smsRecipient.trim()
    const message = smsMessage.trim()

    if (!selectedAlias || !smsCountryId || !recipientNumber || !message) return

    setSmsError(null)
    sendSmsMutation.mutate({
      aliasId: selectedAlias,
      recipientCountryId: smsCountryId,
      recipientNumber,
      message,
    })
  }

  const aliasOptions =
    aliases?.map(alias => ({
      value: alias.id,
      label: alias.alias,
    })) || []

  const isSmsSending = sendSmsMutation.isLoading
  const isSmsDisabled =
    isSmsSending || !selectedAlias || !smsCountryId || !smsRecipient.trim() || !smsMessage.trim()
  const smsCountryOptions = countryOptions.map(option => ({
    value: option.value,
    label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
  }))

  return (
    <>
      <h2>
        <Translation id={getInjuryReportStatusTextId(statusId)} />
      </h2>

      <InjuryReportOptions
        injuryReportId={Number(id)}
        statusId={statusId}
        onSendSmsClick={aliases && aliases.length > 0 ? handleOpenSmsModal : undefined}
      />

      {aliases && aliases.length > 0 && (
        <Popup
          ariaLabel={translate('Send sms')}
          isOpen={isSmsModalOpen}
          variant="narrow"
          onClose={() => !isSmsSending && setIsSmsModalOpen(false)}
        >
          <div className="flex flex-col gap-4">
            <h2>
              <Translation id="Send sms" />
            </h2>

            {aliases.length > 1 ? (
              <Select
                id="injury-report-sms-sender"
                className="w-full"
                options={aliasOptions}
                value={selectedAlias || ''}
                label={<Translation id="Sender:" />}
                onChange={value => {
                  if (typeof value?.value !== 'number') return
                  setSelectedAlias(value.value)
                }}
              />
            ) : (
              <Input
                id="injury-report-sms-sender"
                isFullWidth
                wrapperClassName="w-full"
                label={<Translation id="Sender:" />}
                value={aliases[0]?.alias || ''}
                isDisabled
              />
            )}

            <div className="flex w-full flex-col gap-1">
              <label htmlFor="injury-report-sms-recipient">
                <Translation id="To:" />
              </label>

              <div className="flex w-full gap-2">
                <Select
                  id="injury-report-sms-country"
                  options={smsCountryOptions}
                  value={smsCountryId || ''}
                  ariaLabel={translate('Country code')}
                  isDisabled={isSmsSending}
                  onChange={option => {
                    if (typeof option?.value !== 'number') return
                    setSmsCountryId(option.value)
                  }}
                />

                <Input
                  id="injury-report-sms-recipient"
                  isFullWidth
                  wrapperClassName="grow"
                  ariaLabel={translate('Phone number')}
                  value={smsRecipient}
                  isDisabled={isSmsSending}
                  onChange={e => setSmsRecipient(e.target.value)}
                  whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                />
              </div>
            </div>

            <Textarea
              id="injury-report-sms-message"
              className="w-full resize-none"
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              value={smsMessage}
              onChange={e => setSmsMessage(e.target.value)}
              isDisabled={isSmsSending}
              placeholder={translate('New message')}
              rows={5}
            />

            {smsError && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {smsError}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleSendSms}
                disabled={isSmsDisabled}
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              >
                {isSmsSending ? (
                  <LoaderCircle
                    className="h-4 w-4"
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                  />
                ) : (
                  <Translation id="Send sms" />
                )}
              </Button>
            </div>
          </div>
        </Popup>
      )}

      <div className="sections">
        <section>
          <DataPoints>
            <h3>
              <Translation id="User" />
            </h3>

            <DataPoint header={<Translation id="Name" />} value={userName} />

            <DataPoint
              header={<Translation id="Phone number" />}
              value={getFormatterMobileNumber(phoneNumber)}
            />
          </DataPoints>
        </section>

        <section>
          <DataPoints>
            <h3>
              <Translation id="Asset" />
            </h3>

            <DataPoint header={<Translation id="Description" />} value={assetDescription} />

            <DataPoint header={<Translation id="Imei/snr" />} value={assetImeiSnr} />
          </DataPoints>
        </section>

        <section>
          <DataPoints>
            <h3>
              <Translation id="Injury" />
            </h3>

            <DataPoint header={<Translation id="Type" />} value={type} />

            <DataPoint header={<Translation id="Location" />} value={damageLocation} />

            <DataPoint
              header={<Translation id="Date" />}
              value={<LocalDateTime date={injuryDate} />}
            />

            <DataPoint header={<Translation id="Cause" />} value={damageCause} />

            <DataPoint header={<Translation id="Description" />} value={damageDescription} />
          </DataPoints>
        </section>

        <section>
          <DataPoints>
            <h3>
              <Translation id="Injury Report" />
            </h3>

            <DataPoint
              header={<Translation id="Created" />}
              value={<LocalDateTime date={created} />}
            />

            <DataPoint
              header={<Translation id="Updated" />}
              value={<LocalDateTime date={updated} />}
            />

            <DataPoint header={<Translation id="Created by" />} value={creatorName || userName} />

            <DataPoint header={<Translation id="Updated by" />} value={editorName || userName} />

            {insuranceRef && (
              <DataPoint header={<Translation id="Insurance ref" />} value={insuranceRef} />
            )}

            {policeReportRef && (
              <DataPoint header={<Translation id="Police ref" />} value={policeReportRef} />
            )}

            <DataPoint
              header={<Translation id="Form" />}
              value={
                <ExternalLink withIcon href={FORM_URL.replace(':code', code)}>
                  <Translation id="View" />
                </ExternalLink>
              }
            />
          </DataPoints>
        </section>
        <section className="w-1/2">
          <DataPoints>
            <h3>
              <Translation id="Dealer comments" />
            </h3>

            <div className="flex w-full flex-col gap-2">
              {/* Text area and button container */}
              <div className="flex w-full gap-2">
                <div className="flex-1">
                  <Textarea
                    id="dealer_comments"
                    className="resize-none"
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                    value={comment}
                    onChange={handleCommentChange}
                    isDisabled={isSubmitting}
                    placeholder="Enter your comment here..."
                    maxLength={2000}
                    rows={4}
                  />
                </div>
                <div className="flex w-[10%] items-center justify-center px-2">
                  <Button
                    onClick={handleAddComment}
                    disabled={isSubmitting || !comment.trim()}
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                  >
                    {isSubmitting ? (
                      <>
                        <LoaderCircle
                          className="mr-1 h-4 w-4"
                          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                        />
                        <span className="text-xs">Adding...</span>
                      </>
                    ) : (
                      <span className="text-center text-xs leading-tight">
                        <Translation id="Add Comment" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {/* Character counter */}
              <div className="self-end text-sm text-gray-500">{comment.length}/2000</div>

              {/* Error message */}
              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}

              {dealerComment && (
                <div className="mt-2">
                  <DataPoint
                    header={<Translation id="Existing comment" />}
                    value={
                      <div className="space-y-3">
                        {dealerComment
                          .split(/\n\s*\n/)
                          .map((comment, index) => {
                            const result = extractCommentFromInjuryReport(comment)

                            if (!result) return null

                            const { timestamp, username, commentText } = result

                            if (timestamp) {
                              return (
                                <div key={index} className="border-l-4 border-gray-200 py-2 pl-4">
                                  <div className="mb-1 text-sm text-gray-600">
                                    <span className="font-medium">ID: {username}</span>
                                    <span className="ml-2 text-gray-400">Date: {timestamp}</span>
                                  </div>
                                  <div className="overflow-wrap-anywhere whitespace-pre-wrap break-words text-gray-800">
                                    {commentText}
                                  </div>
                                </div>
                              )
                            } else {
                              // Handle legacy comments without timestamp format
                              return (
                                <div key={index} className="border-l-4 border-gray-300 py-2 pl-4">
                                  <div className="overflow-wrap-anywhere whitespace-pre-wrap break-words text-gray-800">
                                    {commentText}
                                  </div>
                                </div>
                              )
                            }
                          })
                          .filter(Boolean)}
                      </div>
                    }
                  />
                </div>
              )}
            </div>
          </DataPoints>
        </section>
      </div>
    </>
  )
}

export default InjuryReport
